/**
 * src/hooks/useOnboardingStatus.ts — derive a customer's onboarding state.
 *
 * Onboarding is "complete" once a customer has all three of:
 *   1. a saved delivery address,
 *   2. an active card on file, and
 *   3. at least one crate ordered (a pending order counts).
 *
 * Completion is derived from SERVER state (not a localStorage flag) so the
 * gated welcome flow is resumable across devices and can't be faked. The
 * three signals come from the same hooks the dashboard already uses.
 *
 * NOTE (Phase 1): serviceability (is the address in a Denver cluster?) is
 * intentionally NOT gated here. The server geocoder is Nominatim, which
 * "misses" on hand-typed addresses (see resources/_geocode.js) — gating on
 * cluster_id would wrongly turn away valid customers. The out-of-area →
 * waitlist branch lands in Phase 2 alongside Places autocomplete, which
 * supplies precise coordinates.
 */

import { useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCustomerProfile } from './useCustomerProfile';
import { usePaymentStatus } from './usePaymentStatus';
import { useCrates } from './useCrates';
import { useCustomerOrders } from './useCustomerOrders';
import type { Customer } from '../types';

/** The next action a customer must take, in order. */
export type OnboardingStep = 'address' | 'payment' | 'order' | 'complete';

export interface OnboardingStatus {
  /** True while any of the underlying fetches is still in flight. */
  loading: boolean;
  hasAddress: boolean;
  hasCard: boolean;
  hasOrder: boolean;
  /** All three requirements satisfied. */
  complete: boolean;
  /** The first unmet requirement (or 'complete'). */
  step: OnboardingStep;
  /** The customer record (for prefilling the address form on resume). */
  profile: Customer | null;
  /** Re-fetch every underlying signal (call after a step succeeds). */
  refetch: () => void;
}

export function useOnboardingStatus(): OnboardingStatus {
  const { customerEmail, token } = useAuth();
  const { profile, loading: profileLoading, refetch: refetchProfile } = useCustomerProfile();
  const { pm, isLoading: pmLoading, refetch: refetchPm } = usePaymentStatus();
  const { crates, isLoading: cratesLoading, refetch: refetchCrates } = useCrates(customerEmail, token);
  const { orders, isLoading: ordersLoading, refetch: refetchOrders } = useCustomerOrders(customerEmail, token);

  const hasAddress = !!profile?.address && profile.address.trim().length > 0;
  const hasCard = pm?.payment_method_status === 'active';
  // A materialized crate OR any non-cancelled order both count as "ordered".
  const hasOrder = crates.length > 0 || orders.some((o) => o.status !== 'cancelled');

  // Progressive loading: we only need each signal up to the FIRST unmet
  // requirement. A customer with no address is at step 'address' no matter
  // what payment/crates/orders say — so don't block (or hang) the gate + the
  // welcome flow waiting on the slow crates/orders queries when the step is
  // already decided. Those two were the CI laggards: when the cluster is slow
  // they didn't finish inside the 60s e2e budget and WelcomeFlow sat on its
  // loading spinner forever even though the address step was knowable in ms.
  const progressiveLoading = profileLoading
    ? true
    : !hasAddress
      ? false
      : pmLoading
        ? true
        : !hasCard
          ? false
          : cratesLoading || ordersLoading;

  // Latch the (progressive) loading to its first resolution. The underlying
  // hooks refetch in the background (payment on visibility/pageshow, orders on
  // the WS subscription, profile on roleVersion, crates on token change); if
  // `loading` tracked them live it would flap false→true→false and make
  // OnboardingCompleteRoute oscillate between <LoadingFallback/> and the
  // /welcome redirect, never settling. Once decided, the derived
  // hasAddress/hasCard/hasOrder stay live; only `loading` is frozen false.
  const loadedOnceRef = useRef(false);
  if (!progressiveLoading) loadedOnceRef.current = true;
  const loading = !loadedOnceRef.current;
  const complete = hasAddress && hasCard && hasOrder;
  const step: OnboardingStep = !hasAddress
    ? 'address'
    : !hasCard
      ? 'payment'
      : !hasOrder
        ? 'order'
        : 'complete';

  const refetch = useCallback(() => {
    refetchProfile();
    refetchPm();
    refetchCrates();
    refetchOrders();
  }, [refetchProfile, refetchPm, refetchCrates, refetchOrders]);

  return { loading, hasAddress, hasCard, hasOrder, complete, step, profile: profile ?? null, refetch };
}
